import { ContentCopy, InfoOutlined } from "@mui/icons-material";
import { Typography, useTheme } from "@mui/material";
import { useFeatureGate } from "@statsig/react-bindings";

import { LoadingButton } from "@/Components/v2/atoms/Button";
import { Stack } from "@/Components/v2/atoms/Stack";
import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";

import { useDuplicateScenario } from "../../../api/duplicateScenario";

type ScenarioErrorStateProps = {
  scenarioId: string;
  error: string;
};

const ErrorMap: Record<
  string,
  { errorTitle: string; errorDescription: string; showCloneCTA: boolean }
> = {
  "Scenario simulation time exceeded limit of 60 minutes": {
    errorTitle: "Scenario timed out",
    errorDescription:
      "This scenario took too long to complete and was stopped automatically. Try duplicating it and running it again. If the issue continues, please reach out to your Marketing Science Partner for support.",
    showCloneCTA: true,
  },
  "There are no possible scenarios to generate based on the constraints.": {
    errorTitle: "No scenarios could be generated",
    errorDescription:
      "The current inputs are too restrictive. Try duplicating this scenario and loosening one or more constraints to generate results.",
    showCloneCTA: true,
  },
};

export const ScenarioErrorState = ({ scenarioId, error }: ScenarioErrorStateProps) => {
  const theme = useTheme();
  const { value: isScenariosApiEnabled } = useFeatureGate(featureFlag.scenarioBuilderApi);

  const errorData = ErrorMap[error] ?? {
    errorTitle: "Something went wrong",
    errorDescription:
      "Sorry, we couldn't run this scenario. Please contact your Marketing Science Partner for support.",
    showCloneCTA: false,
  };

  const duplicateScenario = useDuplicateScenario({ isScenariosApiEnabled });

  const handleDuplicateScenario = () => {
    duplicateScenario.mutate(scenarioId);
  };

  return (
    <Stack
      gap={1}
      flexDirection="row"
      alignItems="center"
      bgcolor={
        theme.palette.mode === "light" ? theme.palette.error.light : theme.palette.error.dark
      }
      p={3}
      borderRadius={3}
    >
      <InfoOutlined
        sx={{
          alignSelf: "flex-start",
        }}
      />
      <Stack spacing={1} alignItems="start" maxWidth="672px">
        <Typography variant="body1" fontWeight={600}>
          {errorData.errorTitle}
        </Typography>

        <Typography variant="body1" color="text.secondary">
          {errorData.errorDescription}
        </Typography>
      </Stack>
      {!isScenariosApiEnabled && errorData.showCloneCTA && (
        <LoadingButton
          startIcon={<ContentCopy />}
          variant="contained"
          color="primary"
          onClick={handleDuplicateScenario}
          sx={{
            ml: "auto",
          }}
        >
          Duplicate Scenario
        </LoadingButton>
      )}
    </Stack>
  );
};
